import styles from 'css/src/components/common/BookProductCallCta.module.scss';

import React, { useCallback, useState } from 'react';

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS,
} from 'js/src/components/common/ui/Button';
import { PRODUCT_TEAM_CALL_URL } from 'js/src/libs/consts';
import { getServiceLevel, ServiceLevel } from 'js/src/libs/server-utils';

const HEADINGS = [
    'Got ideas for how to make Lumen5 better?',
    'Want the next version built around your use case?',
    'Help us make Lumen5 better!',
];

const AVATARS = [
    { initials: 'AK', className: styles['avatar-1'] },
    { initials: 'RM', className: styles['avatar-2'] },
    { initials: 'JT', className: styles['avatar-3'] },
];

export enum BookProductCallCtaVariant {
    /** Full-size card, for roomy surfaces like a modal body. */
    CARD = 'card',
    /** Tighter card for narrow surfaces like the download popover. */
    COMPACT = 'compact',
}

interface BookProductCallCtaProps {
    /** Where the CTA is rendered, sent with the analytics events so surfaces can be compared. */
    surface: string;
    /**
     * Spacing from the surrounding surface. Applied here rather than by a wrapper so it
     * disappears along with the CTA when it renders nothing.
     */
    className?: string;
    variant?: BookProductCallCtaVariant;
    /** Renders a close button that calls this. Omit for a non-dismissable CTA. */
    onDismiss?: () => void;
    /** Called after the booking link is opened, e.g. to close the surrounding modal. */
    onBook?: () => void;
}

/**
 * Invitation to book a call with the product team. Only shown to high-service users, so callers
 * don't need to gate on service level themselves -- it renders nothing for everyone else.
 */
export const BookProductCallCta: React.FC<BookProductCallCtaProps> = ({
    surface,
    className = '',
    variant = BookProductCallCtaVariant.CARD,
    onDismiss,
    onBook,
}) => {
    const isCompact = variant === BookProductCallCtaVariant.COMPACT;
    const [heading] = useState(() => HEADINGS[Math.floor(Math.random() * HEADINGS.length)]);

    const onClickBook = useCallback(() => {
        analytics.track('Product Call CTA Clicked', { surface });
        onBook?.();
    }, [surface, onBook]);

    const onClickDismiss = useCallback(() => {
        analytics.track('Product Call CTA Dismissed', { surface });
        onDismiss?.();
    }, [surface, onDismiss]);

    if (getServiceLevel() !== ServiceLevel.HIGH) {
        return null;
    }

    return (
        <div
            className={`${styles['cta']} ${
                isCompact ? styles['compact'] : ''
            } position-relative d-flex gap-3 ${isCompact ? 'p-l5-12' : 'p-l5-16'} ${className}`}
        >
            <div className="d-flex flex-shrink-0" aria-hidden="true">
                {AVATARS.map(({ initials, className }) => (
                    <div
                        key={initials}
                        className={`${styles['avatar']} ${className} position-relative d-flex flex-shrink-0 align-items-center justify-content-center rounded-circle text-white fw-semibold`}
                    >
                        {initials}
                    </div>
                ))}
            </div>
            <div className={`${styles['content']} d-flex flex-column align-items-start`}>
                <div className={`fw-semibold ${isCompact ? '' : 'font-l5 font-l5--h6'}`}>
                    {heading}
                </div>
                <div className={`mt-l5-4 ${isCompact ? 'small' : ''}`}>
                    Spend 20 minutes with the people building Lumen5, share your thoughts, and see
                    what&rsquo;s shipping next before anyone else.
                </div>
                <Button
                    className="mt-l5-12"
                    size={isCompact ? BUTTON_SIZES.SM : BUTTON_SIZES.MD}
                    text="Pick a 20-min slot"
                    href={PRODUCT_TEAM_CALL_URL}
                    hrefTarget="_blank"
                    onClick={onClickBook}
                />
            </div>
            {onDismiss && (
                <Button
                    className={`${styles['dismiss']} position-absolute`}
                    size={BUTTON_SIZES.SM}
                    variant={BUTTON_VARIANTS.TEXT}
                    color={BUTTON_COLORS.SIMPLE}
                    iconLeft="close"
                    useIconButtonPresets={true}
                    text={<span className="visually-hidden">Dismiss</span>}
                    tooltipTitle="Dismiss"
                    onClick={onClickDismiss}
                />
            )}
        </div>
    );
};
